import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  BadgePercent,
  Ban,
  CreditCard,
  IndianRupee,
  Percent,
  Receipt,
  Repeat,
  RotateCcw,
  ShoppingBag,
  Truck,
  TrendingUp,
  Wallet,
} from "lucide-react";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { RefreshBar } from "@/components/admin/RefreshBar";
import {
  AnalyticsExportButtons,
  AnalyticsFilters,
  ChartFrame,
  DataTable,
  HourDayHeatmap,
  KpiCard,
  MetricBars,
  Panel,
  PIE_TONES,
  TONE_VAR,
  compactInr,
  inr,
  num,
  pct,
  pct0,
  tooltipStyle,
} from "@/components/admin/analytics/AnalyticsKit";
import { getSalesAnalytics } from "@/lib/admin/analytics-suite.functions";
import type { SuiteRangeKey } from "@/lib/admin/analytics-range";
import type { SalesAnalytics } from "@/lib/admin/sales-analytics.data";

export const Route = createFileRoute("/_admin/admin/sales-analytics")({
  component: SalesAnalyticsPage,
});

function SalesAnalyticsPage() {
  const [range, setRange] = useState<SuiteRangeKey>("30d");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const fetchSales = useServerFn(getSalesAnalytics);

  const q = useQuery({
    queryKey: ["admin", "sales-analytics", range, from, to],
    queryFn: () =>
      fetchSales({ data: { range, ...(range === "custom" ? { from, to } : {}) } }),
    staleTime: 30_000,
    placeholderData: (prev) => prev,
  });

  const d = q.data as SalesAnalytics | undefined;
  const loading = q.isLoading;
  const k = d?.kpis;
  const unverified = new Set(
    (d as { unverifiedKpis?: string[] } | undefined)?.unverifiedKpis ?? [],
  );
  const nm = (key: string, val: string) => (unverified.has(key) ? "Not measured" : val);

  const summaryRows = useMemo(() =>
    (d?.timeseries ?? []).map((p) => ({
      Date: p.date,
      "Paid revenue (₹)": p.gross,
      Orders: p.orders,
    })), [d?.timeseries]);

  const { onExportCsv, onExportExcel } = AnalyticsExportButtons({
    name: "sales-analytics",
    rows: summaryRows,
  });

  return (
    <div>
      <AdminPageHeader
        title="Sales Analytics"
        description="Revenue, payments, discounts and area performance across every ordering channel."
      />

      <RefreshBar
        targets={[{ key: "sales-analytics", label: "sales" }]}
        busy={q.isFetching}
        lastUpdated={q.dataUpdatedAt}
      />

      <AnalyticsFilters
        range={range}
        onRangeChange={setRange}
        from={from}
        to={to}
        onFromChange={setFrom}
        onToChange={setTo}
        status={d ? `${d.rangeLabel} · ${(d as { source?: string }).source === "live" ? "Live" : "Not measured"}` : "Loading…"}
        onExportCsv={onExportCsv}
        onExportExcel={onExportExcel}
      />

      {(d as { source?: string } | undefined)?.source !== "live" ? (
        <div role="status" className="rounded-lg border border-border bg-muted p-6 text-sm text-muted-foreground">
          {q.isError ? "Unavailable — the analytics lookup failed." : "Not measured — Sales Analytics is not yet connected to verified live production data. No figures are shown to avoid displaying demo or misleading values."}
        </div>
      ) : null}
      {(d as { source?: string } | undefined)?.source === "live" ? (<>
      {/* KPI cards */}
      <section aria-label="Sales key metrics" className="mb-6">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 xl:grid-cols-6">
          <KpiCard
            label="Gross sales"
            value={inr(k?.grossSales ?? 0)}
            tone="success"
            icon={IndianRupee}
            delta={k?.deltas.grossSales}
            loading={loading}
            to="/admin/reports"
          />
          <KpiCard
            label="Net sales"
            value={nm("netSales", inr(k?.netSales ?? 0))}
            tone="success"
            icon={Wallet}
            delta={k?.deltas.netSales}
            loading={loading}
            to="/admin/reports"
          />
          <KpiCard
            label="Total orders"
            value={num(k?.orders ?? 0)}
            tone="teal"
            icon={ShoppingBag}
            delta={k?.deltas.orders}
            loading={loading}
            to="/admin/orders"
          />
          <KpiCard
            label="Paid orders"
            value={num(k?.paidOrders ?? 0)}
            tone="success"
            icon={CreditCard}
            loading={loading}
            to="/admin/orders"
          />
          <KpiCard
            label="Average order value"
            value={inr(k?.aov ?? 0)}
            tone="info"
            icon={TrendingUp}
            delta={k?.deltas.aov}
            loading={loading}
          />
          <KpiCard
            label="Refund amount"
            value={nm("refundAmount", inr(k?.refundAmount ?? 0))}
            tone="danger"
            icon={RotateCcw}
            delta={k?.deltas.refundAmount}
            loading={loading}
            to="/admin/returns"
          />
          <KpiCard
            label="Discount given"
            value={inr(k?.discountGiven ?? 0)}
            tone="warn"
            icon={BadgePercent}
            delta={k?.deltas.discountGiven}
            loading={loading}
          />
          <KpiCard
            label="Tax collected"
            value={inr(k?.taxCollected ?? 0)}
            tone="purple"
            icon={Receipt}
            delta={k?.deltas.taxCollected}
            loading={loading}
          />
          <KpiCard
            label="Delivery charges"
            value={inr(k?.deliveryCharges ?? 0)}
            tone="neutral"
            icon={Truck}
            delta={k?.deltas.deliveryCharges}
            loading={loading}
            to="/admin/deliveries"
          />
          <KpiCard
            label="Payment success"
            value={nm("paymentSuccess", pct(k?.paymentSuccess ?? 0))}
            tone="success"
            icon={CreditCard}
            delta={k?.deltas.paymentSuccess}
            loading={loading}
          />
          <KpiCard
            label="Cancelled orders"
            value={num(k?.cancelledOrders ?? 0)}
            tone="danger"
            icon={Ban}
            delta={k?.deltas.cancelledOrders}
            loading={loading}
            to="/admin/orders"
          />
          <KpiCard
            label="Repeat purchase revenue"
            value={nm("repeatRevenue", inr(k?.repeatRevenue ?? 0))}
            tone="teal"
            icon={Repeat}
            delta={k?.deltas.repeatRevenue}
            loading={loading}
            to="/admin/customer-analytics"
          />
          <KpiCard
            label="Subscription revenue"
            value={nm("subscriptionRevenue", inr(k?.subscriptionRevenue ?? 0))}
            tone="purple"
            icon={Percent}
            delta={k?.deltas.subscriptionRevenue}
            loading={loading}
            to="/admin/meal-plans-v2"
          />
        </div>
      </section>

      {/* Revenue trend + daily sales */}
      <div className="mb-6 grid grid-cols-1 gap-4 xl:grid-cols-3">
        <Panel title="Revenue trend" subtitle="Gross vs net" className="xl:col-span-2">
          <ChartFrame loading={loading} empty={(d?.timeseries.length ?? 0) === 0}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={d?.timeseries ?? []}>
                <defs>
                  <linearGradient id="grossFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={TONE_VAR.success} stopOpacity={0.45} />
                    <stop offset="100%" stopColor={TONE_VAR.success} stopOpacity={0.03} />
                  </linearGradient>
                  <linearGradient id="netFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={TONE_VAR.teal} stopOpacity={0.35} />
                    <stop offset="100%" stopColor={TONE_VAR.teal} stopOpacity={0.03} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} stroke="var(--color-muted-foreground)" />
                <YAxis
                  tick={{ fontSize: 11 }}
                  stroke="var(--color-muted-foreground)"
                  tickFormatter={(v: number) => compactInr(v)}
                />
                <Tooltip {...tooltipStyle()} formatter={(v: number) => inr(v)} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Area
                  type="monotone"
                  dataKey="gross"
                  name="Gross"
                  stroke={TONE_VAR.success}
                  strokeWidth={2}
                  fill="url(#grossFill)"
                />
                <Area
                  type="monotone"
                  dataKey="net"
                  name="Net"
                  stroke={TONE_VAR.teal}
                  strokeWidth={2}
                  fill="url(#netFill)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </ChartFrame>
        </Panel>

        <Panel title="Revenue split" subtitle="By channel">
          <ChartFrame loading={loading} empty={(d?.revenueSplit.length ?? 0) === 0}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={d?.revenueSplit ?? []}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={50}
                  outerRadius={85}
                  paddingAngle={2}
                >
                  {(d?.revenueSplit ?? []).map((s, i) => (
                    <Cell key={s.name} fill={TONE_VAR[PIE_TONES[i % PIE_TONES.length]!]} />
                  ))}
                </Pie>
                <Tooltip {...tooltipStyle()} formatter={(v: number) => inr(v)} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          </ChartFrame>
        </Panel>
      </div>

      <div className="mb-6 grid grid-cols-1 gap-4 xl:grid-cols-2">
        <Panel title="Daily sales" subtitle="Orders & revenue per day">
          <ChartFrame loading={loading} empty={(d?.timeseries.length ?? 0) === 0}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={d?.timeseries ?? []}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} stroke="var(--color-muted-foreground)" />
                <YAxis tick={{ fontSize: 11 }} stroke="var(--color-muted-foreground)" />
                <Tooltip {...tooltipStyle()} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="gross" name="Gross ₹" fill={TONE_VAR.success} radius={[3, 3, 0, 0]} />
                <Bar dataKey="orders" name="Orders" fill={TONE_VAR.info} radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartFrame>
        </Panel>

        <Panel title="Monthly sales" subtitle="Rolling six months">
          <ChartFrame loading={loading} empty={(d?.monthly.length ?? 0) === 0}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={d?.monthly ?? []}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="var(--color-muted-foreground)" />
                <YAxis
                  tick={{ fontSize: 11 }}
                  stroke="var(--color-muted-foreground)"
                  tickFormatter={(v: number) => compactInr(v)}
                />
                <Tooltip {...tooltipStyle()} formatter={(v: number) => inr(v)} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="revenue" name="Revenue" fill={TONE_VAR.teal} radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartFrame>
        </Panel>
      </div>

      <div className="mb-6 grid grid-cols-1 gap-4 xl:grid-cols-3">
        <Panel title="Payment analytics" subtitle="Orders by payment state">
          <ChartFrame loading={loading} empty={(d?.payments.length ?? 0) === 0} height="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={d?.payments ?? []}
                  dataKey="value"
                  nameKey="name"
                  outerRadius={85}
                  paddingAngle={1}
                >
                  {(d?.payments ?? []).map((s, i) => (
                    <Cell key={s.name} fill={TONE_VAR[PIE_TONES[i % PIE_TONES.length]!]} />
                  ))}
                </Pie>
                <Tooltip {...tooltipStyle()} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          </ChartFrame>
        </Panel>

        <Panel title="Order source revenue" subtitle="Where the money comes from">
          <MetricBars rows={d?.sourceRevenue ?? []} tone="success" loading={loading} money />
        </Panel>

        <Panel title="Delivery charges analysis" subtitle="Collected vs waived">
          <MetricBars rows={d?.deliveryCharges ?? []} tone="neutral" loading={loading} money />
        </Panel>
      </div>

      <Panel title="Revenue by area" subtitle="Top delivery neighbourhoods" className="mb-6">
        <ChartFrame loading={loading} empty={(d?.areas.length ?? 0) === 0}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={d?.areas ?? []}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis
                dataKey="area"
                tick={{ fontSize: 10 }}
                interval={0}
                angle={-25}
                textAnchor="end"
                height={70}
                stroke="var(--color-muted-foreground)"
              />
              <YAxis
                tick={{ fontSize: 11 }}
                stroke="var(--color-muted-foreground)"
                tickFormatter={(v: number) => compactInr(v)}
              />
              <Tooltip {...tooltipStyle()} formatter={(v: number) => inr(v)} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="revenue" name="Revenue" fill={TONE_VAR.success} radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartFrame>
      </Panel>

      <Panel title="Top revenue generating areas" className="mb-6">
        <DataTable
          rows={d?.areas ?? []}
          loading={loading}
          exportName="revenue-by-area"
          searchOn={(r) => r.area}
          columns={[
            {
              key: "area",
              header: "Area",
              render: (r) => <span className="font-medium text-foreground">{r.area}</span>,
              sortValue: (r) => r.area,
              exportValue: (r) => r.area,
            },
            {
              key: "orders",
              header: "Orders",
              align: "right",
              render: (r) => num(r.orders),
              sortValue: (r) => r.orders,
              exportValue: (r) => r.orders,
            },
            {
              key: "revenue",
              header: "Revenue",
              align: "right",
              render: (r) => inr(r.revenue),
              sortValue: (r) => r.revenue,
              exportValue: (r) => r.revenue,
            },
            {
              key: "aov",
              header: "AOV",
              align: "right",
              render: (r) => inr(r.aov),
              sortValue: (r) => r.aov,
              exportValue: (r) => r.aov,
            },
            {
              key: "growth",
              header: "Growth",
              align: "right",
              render: (r) => (
                <span className={r.growth >= 0 ? "text-ops-success" : "text-ops-danger"}>
                  {r.growth >= 0 ? "▲" : "▼"} {Math.abs(r.growth * 100).toFixed(1)}%
                </span>
              ),
              sortValue: (r) => r.growth,
              exportValue: (r) => `${(r.growth * 100).toFixed(1)}%`,
            },
          ]}
        />
      </Panel>

      <div className="mb-6 grid grid-cols-1 gap-4 xl:grid-cols-2">
        <Panel title="Top revenue generating products">
          <DataTable
            rows={d?.products ?? []}
            loading={loading}
            exportName="top-products"
            searchOn={(r) => `${r.name} ${r.category}`}
            minWidth={520}
            columns={[
              {
                key: "name",
                header: "Product",
                render: (r) => (
                  <div className="flex items-center gap-2">
                    <span
                      className="grid h-8 w-8 shrink-0 place-items-center rounded-md text-[10px] font-semibold"
                      style={{ background: "var(--ops-warn-soft)", color: "var(--ops-warn)" }}
                      aria-hidden
                    >
                      {r.name.slice(0, 2).toUpperCase()}
                    </span>
                    <span className="min-w-0">
                      <span className="block truncate font-medium text-foreground">{r.name}</span>
                      <span className="block text-xs text-muted-foreground">{r.category}</span>
                    </span>
                  </div>
                ),
                sortValue: (r) => r.name,
                exportValue: (r) => r.name,
              },
              {
                key: "revenue",
                header: "Revenue",
                align: "right",
                render: (r) => inr(r.revenue),
                sortValue: (r) => r.revenue,
                exportValue: (r) => r.revenue,
              },
              {
                key: "orders",
                header: "Orders",
                align: "right",
                render: (r) => num(r.orders),
                sortValue: (r) => r.orders,
                exportValue: (r) => r.orders,
              },
              {
                key: "conv",
                header: "Conversion",
                align: "right",
                render: (r) => pct(r.conversion),
                sortValue: (r) => r.conversion,
                exportValue: (r) => pct(r.conversion),
              },
            ]}
          />
        </Panel>

        <Panel title="Top meal plans by revenue">
          <DataTable
            rows={d?.plans ?? []}
            loading={loading}
            exportName="top-meal-plans"
            searchOn={(r) => r.name}
            minWidth={480}
            columns={[
              {
                key: "name",
                header: "Plan",
                render: (r) => <span className="font-medium text-foreground">{r.name}</span>,
                sortValue: (r) => r.name,
                exportValue: (r) => r.name,
              },
              {
                key: "subs",
                header: "Subscribers",
                align: "right",
                render: (r) => num(r.subscribers),
                sortValue: (r) => r.subscribers,
                exportValue: (r) => r.subscribers,
              },
              {
                key: "orders",
                header: "Orders",
                align: "right",
                render: (r) => num(r.orders),
                sortValue: (r) => r.orders,
                exportValue: (r) => r.orders,
              },
              {
                key: "revenue",
                header: "Revenue",
                align: "right",
                render: (r) => inr(r.revenue),
                sortValue: (r) => r.revenue,
                exportValue: (r) => r.revenue,
              },
            ]}
          />
        </Panel>
      </div>

      <Panel title="Discount analysis" subtitle="Coupon cost vs revenue unlocked" className="mb-6">
        <DataTable
          rows={d?.discounts ?? []}
          loading={loading}
          exportName="discount-analysis"
          searchOn={(r) => r.coupon}
          columns={[
            {
              key: "coupon",
              header: "Coupon used",
              render: (r) => (
                <span className="rounded border border-border bg-muted px-2 py-0.5 font-mono text-xs text-foreground">
                  {r.coupon}
                </span>
              ),
              sortValue: (r) => r.coupon,
              exportValue: (r) => r.coupon,
            },
            {
              key: "uses",
              header: "Uses",
              align: "right",
              render: (r) => num(r.uses),
              sortValue: (r) => r.uses,
              exportValue: (r) => r.uses,
            },
            {
              key: "amount",
              header: "Amount",
              align: "right",
              render: (r) => inr(r.amount),
              sortValue: (r) => r.amount,
              exportValue: (r) => r.amount,
            },
            {
              key: "lost",
              header: "Revenue lost",
              align: "right",
              render: (r) => <span className="text-ops-danger">{inr(r.revenueLost)}</span>,
              sortValue: (r) => r.revenueLost,
              exportValue: (r) => r.revenueLost,
            },
            {
              key: "gen",
              header: "Revenue generated",
              align: "right",
              render: (r) => <span className="text-ops-success">{inr(r.revenueGenerated)}</span>,
              sortValue: (r) => r.revenueGenerated,
              exportValue: (r) => r.revenueGenerated,
            },
          ]}
        />
      </Panel>

      <div className="mb-6 grid grid-cols-1 gap-4 xl:grid-cols-2">
        <Panel title="Sales heatmap" subtitle="Hour vs day of week">
          <HourDayHeatmap rows={d?.heatmap ?? []} tone="success" loading={loading} />
        </Panel>

        <Panel title="Refund analysis" subtitle="Refund value by reason">
          <MetricBars rows={d?.refunds ?? []} tone="danger" loading={loading} money />
        </Panel>
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
        <Panel title="Revenue forecast" subtitle="Next 7 days projected" className="xl:col-span-2">
          <ChartFrame loading={loading} empty={(d?.forecast.length ?? 0) === 0}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={d?.forecast ?? []}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} stroke="var(--color-muted-foreground)" />
                <YAxis
                  tick={{ fontSize: 11 }}
                  stroke="var(--color-muted-foreground)"
                  tickFormatter={(v: number) => compactInr(v)}
                />
                <Tooltip {...tooltipStyle()} formatter={(v: number) => inr(v)} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Line
                  type="monotone"
                  dataKey="actual"
                  name="Actual"
                  stroke={TONE_VAR.success}
                  strokeWidth={2}
                  dot={false}
                  connectNulls={false}
                />
                <Line
                  type="monotone"
                  dataKey="forecast"
                  name="Forecast"
                  stroke={TONE_VAR.purple}
                  strokeWidth={2}
                  strokeDasharray="5 4"
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </ChartFrame>
        </Panel>

        <Panel title="Sales comparison" subtitle="Current vs previous period">
          <ul className="space-y-3">
            {(d?.comparison ?? []).map((c) => (
              <li key={c.metric} className="text-sm">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-foreground">{c.metric}</span>
                  <span
                    className={`tabular-nums ${
                      c.growth >= 0 ? "text-ops-success" : "text-ops-danger"
                    }`}
                  >
                    {c.growth >= 0 ? "▲" : "▼"} {Math.abs(c.growth * 100).toFixed(1)}%
                  </span>
                </div>
                <div className="mt-0.5 flex items-center justify-between gap-2 text-xs text-muted-foreground">
                  <span>Current {c.money ? inr(c.current) : num(c.current)}</span>
                  <span>Previous {c.money ? inr(c.previous) : num(c.previous)}</span>
                </div>
              </li>
            ))}
            {loading ? <li className="text-xs text-muted-foreground">Loading…</li> : null}
          </ul>
          <p className="mt-4 text-xs text-muted-foreground">
            Payment success {pct0(k?.paymentSuccess ?? 0)} · cancelled {num(k?.cancelledOrders ?? 0)}{" "}
            orders in {d?.rangeLabel ?? "—"}.
          </p>
        </Panel>
      </div>
      </>) : null}
    </div>
  );
}
